import React, { useState } from 'react'
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom'
import { useQueryParam } from '../../../../shared/hooks/location'
import { useApiFormRegisterUser } from '../../../../api-new/state/content'
import { authPath, carryParams, domainOf } from '../hooks/auth-path'
import { AuthHead, AuthMark, AuthNotice, AuthPage } from '../../../../shared/sso-workspace/auth-card'
import { ErrorReference } from '../../../../shared/error/error-reference'

// /auth/register: create an EducationXR account with a password. Reached
// only from /auth/email when the address typed there has no account; opened
// without one (the old unstyled /public/register redirects here), it goes to
// sign-in instead.
//
// The address that wasn't found arrives in router state ({ email }), not the
// query string, so it stays out of the URL, server logs and Referer headers.
// It is read-only here (owner 2026-10-22): sign-in checked that it has no
// account, and changing it on this form could name one that does. "Use a
// different email" goes back to sign-in, which checks the new address.
// Browser history keeps router state, so a reload keeps the prefill too.
// Only an email-shaped value is carried: the sign-in field also takes
// usernames, which make no sense as a registration email.
//
// Where to go after signing in (redirect=, redirect-doc=: a course link's
// sign-in, owner 2026-10-13) arrives in the query, so a reload keeps it, and
// every way back to signing in carries it on ("I already have an account",
// "Back to sign in", the SSO sign-in). Confirming the email happens
// elsewhere; "Check your email" tells them to come back here and sign in,
// which then goes straight to the course (the launch bridge).
export const registerState = value => {
  const email = (value || '').trim()

  return domainOf(email) ? { email } : undefined
}

// The rule the shared password field enforces elsewhere (FormPasswordValidate):
// at least 8 characters, no spaces. '' when the value is acceptable.
export const passwordProblem = value => {
  if (!value) {
    return 'Enter a password.'
  }

  if (/\s/.test(value)) {
    return "Passwords can't contain spaces."
  }

  if (value.length < 8) {
    return 'Use at least 8 characters.'
  }

  return ''
}

// What "Check your email" adds when the person came from somewhere: a
// course link (the launch bridge) or another destination. '' when none.
export const comeBackSentence = (redirect, redirectDoc) => {
  if (!redirect && !redirectDoc) {
    return ''
  }

  let target = ''
  try {
    target = redirect ? atob(redirect) : ''
  } catch {
    target = ''
  }

  return target.startsWith('/launch/content/')
    ? 'Then come back to this page and sign in to go straight to your course.'
    : 'Then come back to this page and sign in to carry on where you left off.'
}

// The sentence the register-user service refuses an SSO-domain email with
// (409 + {message}, no code), or '' for any other failure.
export const ssoRefusalMessage = err =>
  (err && err.status === 409 && err.data && !err.data.code && typeof err.data.message === 'string' && err.data.message) || ''

// content's own sign-up conflicts (409 + {code, message}, 2026-10-21): an
// account already on the email, or a sign-up for it already on its way (a
// second click raced the first).
export const LOGIN_EXISTS = 'register.login_exists'
export const IN_PROGRESS = 'register.in_progress'
const conflictCode = err => (err && err.status === 409 && err.data && err.data.code) || ''

export const Register = () => {
  const history = useHistory()
  const location = useLocation()

  const carried = (location.state && location.state.email) || ''

  const redirect = useQueryParam('redirect')
  const redirectDoc = useQueryParam('redirect-doc')
  const comeBack = comeBackSentence(redirect, redirectDoc)
  const signInWith = params => carryParams({ redirect, redirectDoc, ...params })

  const apiFormRegisterUser = useApiFormRegisterUser()

  const [email, updateEmail] = useState(carried)
  const [password, updatePassword] = useState('')
  const [revealPassword, updateRevealPassword] = useState(false)
  const [firstName, updateFirstName] = useState('')
  const [lastName, updateLastName] = useState('')
  const [companyName, updateCompanyName] = useState('')
  const [submitting, updateSubmitting] = useState(false)
  const [failed, updateFailed] = useState(false)
  const [exists, updateExists] = useState('')
  const [registered, updateRegistered] = useState(null)
  // The server's refusal for an email whose organization signs in with SSO
  // (409, spec 2026-09-27-sso-domain-registration-and-invites-design): its
  // sentence, and the email it was for.
  const [ssoRefusal, updateSsoRefusal] = useState(null)

  const problem = password ? passwordProblem(password) : ''

  const ready = !!email.trim() && !!domainOf(email) && !passwordProblem(password) && !!firstName.trim() && !!lastName.trim()

  const signInPath = authPath('/auth/email', signInWith({ email: email.trim() }))

  const handleSubmit = async e => {
    e.preventDefault()

    if (!ready || submitting) {
      return
    }

    updateSubmitting(true)
    updateFailed(false)
    updateExists('')

    const data = {
      email: email.trim(),
      password,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      companyName: companyName.trim()
    }

    try {
      await apiFormRegisterUser.post(data)
      updateRegistered(data.email)
    } catch (err) {
      const message = ssoRefusalMessage(err)
      const conflict = conflictCode(err)

      if (message) {
        updateSsoRefusal({ email: data.email, message })
      } else if (conflict === IN_PROGRESS) {
        // the first click's confirmation email is on its way
        updateRegistered(data.email)
      } else if (conflict === LOGIN_EXISTS) {
        updateExists(err.data.message || 'An account with this email already exists. Sign in instead.')
      } else {
        // the failure itself, for its reference (error-reference.js)
        updateFailed(err || true)
      }
    } finally {
      updateSubmitting(false)
    }
  }

  // Sign-up follows sign-in's check only (owner 2026-10-22): opened with no
  // address sign-in found no account for (a bookmark, an old /public/register
  // link), it goes to sign-in, which checks the address first.
  if (!carried) {
    return <Redirect to={authPath('/auth/email', signInWith({}))} />
  }

  if (ssoRefusal) {
    return (
      <AuthPage labelledBy="register-sso-title">
        <AuthHead id="register-sso-title" title="Sign in with your organization" />
        <AuthNotice tone="warn" role="alert" id="register-sso">
          <p>{ssoRefusal.message}</p>
          <p className="m-0">
            Signing in with your organization creates your EducationXR account for <strong>{ssoRefusal.email}</strong>. There's no password to set.
          </p>
        </AuthNotice>
        <button
          type="button"
          className="btn sw-btn-primary sa-block"
          onClick={() => history.replace(authPath('/auth/providers', signInWith({ email: ssoRefusal.email })))}>
          Sign in with your organization
        </button>
        <button type="button" className="btn sw-btn sa-block" onClick={() => updateSsoRefusal(null)}>
          Use a different email
        </button>
      </AuthPage>
    )
  }

  if (registered) {
    return (
      <AuthPage labelledBy="register-done-title">
        <AuthHead id="register-done-title" title="Check your email" mark={<AuthMark tone="ok">✓</AuthMark>} />
        <p className="m-0">
          We've sent a confirmation link to <strong>{registered}</strong>. {comeBack ? 'Open it to finish creating your account.' : 'Open it to finish creating your account, then sign in.'}
        </p>
        {comeBack && <p className="m-0">{comeBack}</p>}
        <p className="m-0 sw-hint">Nothing there after a few minutes? Check your spam or junk folder.</p>
        <Link replace className="btn sw-btn sa-block" to={authPath('/auth/email', signInWith({ email: registered }))}>Back to sign in</Link>
      </AuthPage>
    )
  }

  return (
    <AuthPage labelledBy="register-title">
      <AuthHead id="register-title" title="Create your account" lede="Sign up with your email and a password." />

      {carried &&
        <AuthNotice id="register-not-found">
          <p>
            We couldn't find an account for <strong>{carried}</strong>. Create one below, or use a different email.
          </p>
        </AuthNotice>}

      {exists &&
        <AuthNotice tone="warn" role="alert" id="register-exists">
          <p className="m-0">{exists}</p>
        </AuthNotice>}

      {failed &&
        <AuthNotice tone="bad" role="alert">
          <p>We couldn't create the account. If you already have one, sign in instead; otherwise check the details and try again.</p>
          <ErrorReference error={failed} />
        </AuthNotice>}

      <form className="sa-form" noValidate onSubmit={handleSubmit}>
        <div className="sw-field">
          <label className="sw-label" htmlFor="register-email">Email</label>
          <input
            id="register-email"
            name="email"
            className="form-control"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="name@example.org"
            autoFocus={!carried}
            value={email}
            readOnly={!!carried}
            onChange={e => !carried && updateEmail(e.target.value)}
          />
          {carried &&
            <button type="button" className="btn btn-link p-0 small" onClick={() => history.replace(authPath('/auth/email', signInWith({})))}>
              Use a different email
            </button>}
        </div>

        <div className="sw-field">
          <label className="sw-label" htmlFor="register-first-name">First name</label>
          <input
            id="register-first-name"
            name="firstName"
            className="form-control"
            type="text"
            autoComplete="given-name"
            autoFocus={!!carried}
            value={firstName}
            onChange={e => updateFirstName(e.target.value)}
          />
        </div>

        <div className="sw-field">
          <label className="sw-label" htmlFor="register-last-name">Last name</label>
          <input
            id="register-last-name"
            name="lastName"
            className="form-control"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={e => updateLastName(e.target.value)}
          />
        </div>

        <div className="sw-field">
          <label className="sw-label" htmlFor="register-company">Organization (optional)</label>
          <input
            id="register-company"
            name="companyName"
            className="form-control"
            type="text"
            autoComplete="organization"
            value={companyName}
            onChange={e => updateCompanyName(e.target.value)}
          />
        </div>

        <div className="sw-field">
          <label className="sw-label" htmlFor="register-password">Password</label>
          <div className="input-group">
            <input
              id="register-password"
              name="password"
              className="form-control"
              type={revealPassword ? 'text' : 'password'}
              autoComplete="new-password"
              aria-describedby="register-password-hint"
              aria-invalid={!!problem}
              value={password}
              onChange={e => updatePassword(e.target.value)}
            />
            <button
              type="button"
              className="btn sw-btn"
              aria-controls="register-password"
              aria-label={revealPassword ? 'Hide password' : 'Show password'}
              onClick={() => updateRevealPassword(!revealPassword)}>
              {revealPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <p id="register-password-hint" className="m-0 sw-hint">
            {problem || 'Use at least 8 characters, with no spaces.'}
          </p>
        </div>

        <button type="submit" className="btn sw-btn-primary sa-block" disabled={!ready || submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <button type="button" className="btn sw-btn sa-block" onClick={() => history.replace(signInPath)}>
        I already have an account
      </button>
    </AuthPage>
  )
}
